import type { Color, Config } from '@ml/config';

/**
 * Công thức sát thương số nguyên (combat.md 1, 3, 4; orbs.md 5.3). Mỗi phép nhân-chia lấy
 * `floor` ngay tại bước đó, đúng thứ tự viết trong GDD. Mọi đầu vào là số nguyên ≥ 0.
 */
export type CombatConfig = Pick<Config, 'combat' | 'link'>;
export type Element = Color | 'none';

/** Hệ số khắc hệ (combat.md 3.4), dạng phần trăm int. */
export function elemPct(attack: Element, target: Element, config: Pick<Config, 'combat'>): number {
  if (attack === 'none' || target === 'none') return 100;
  const e = config.combat.element;
  if ((e.beats[attack] ?? []).includes(target)) return e.advantagePct;
  if ((e.beats[target] ?? []).includes(attack)) return e.disadvantagePct;
  return 100;
}

/**
 * Bước phòng thủ: phần thô > 0 thì `max(minDamage, floor(raw × scale / (scale + def)))`;
 * phần thô = 0 giữ 0 (combat.md 1, D12).
 */
export function applyDefense(raw: number, def: number, config: Pick<Config, 'combat'>): number {
  if (raw <= 0) return 0;
  const s = config.combat.defenseScale;
  return Math.max(config.combat.minDamage, Math.floor((raw * s) / (s + def)));
}

export type DamageSteps = { readonly steps: readonly number[]; readonly value: number };

/**
 * Bonus độ dài (%, combat.md 3.1) có giảm dần: viên vượt `link.minLength` thứ nhất được
 * `lengthBonusPct`, mỗi viên sau được `floor(bonus viên trước × lengthBonusDecayPct / 100)`.
 * `lengthBonusDecayPct = 100` → tuyến tính như cũ; `0` → chỉ viên vượt đầu tiên có bonus.
 */
export function lengthExtraPct(length: number, config: CombatConfig): number {
  let step = config.combat.lengthBonusPct;
  let extra = 0;
  for (let k = config.link.minLength; k < length && step > 0; k++) {
    extra += step;
    step = Math.floor((step * config.combat.lengthBonusDecayPct) / 100);
  }
  return extra;
}

/** combat.md 3.1. `critDmgPct` đã được chọn (hero hoặc mặc định combat). */
export function normalMainDamage(
  p: {
    handAttack: number;
    length: number;
    color: Element;
    crit: boolean;
    critDmgPct: number;
    targetElement: Element;
    targetDef: number;
  },
  config: CombatConfig,
): DamageSteps {
  const extra = lengthExtraPct(p.length, config);
  const b1 = Math.floor((p.handAttack * p.length * (100 + extra)) / 100);
  const b2 = Math.floor((b1 * elemPct(p.color, p.targetElement, config)) / 100);
  const b3 = p.crit ? Math.floor((b2 * p.critDmgPct) / 100) : b2;
  const main = applyDefense(b3, p.targetDef, config);
  return { steps: [b1, b2, b3, main], value: main };
}

/** combat.md 4.2. `powerPct = 0` → không có phần chính (0). */
export function abilityMainDamage(
  p: {
    abilityAttack: number;
    powerPct: number;
    element: Element;
    crit: boolean;
    critDmgPct: number;
    targetElement: Element;
    targetRes: number;
  },
  config: Pick<Config, 'combat'>,
): DamageSteps {
  const a1 = Math.floor((p.abilityAttack * p.powerPct) / 100);
  const a2 = Math.floor((a1 * elemPct(p.element, p.targetElement, config)) / 100);
  const a3 = p.crit ? Math.floor((a2 * p.critDmgPct) / 100) : a2;
  const main = applyDefense(a3, p.targetRes, config);
  return { steps: [a1, a2, a3, main], value: main };
}

/** combat.md 3.2: không khắc hệ, không crit. C = 0 → 0. */
export function collateralDamage(
  handAttack: number,
  count: number,
  targetDef: number,
  config: Pick<Config, 'combat'>,
): number {
  if (count <= 0) return 0;
  const c1 = Math.floor((handAttack * count * config.combat.collateralPct) / 100);
  return applyDefense(c1, targetDef, config);
}

/** Tier ngọc ấn (orbs.md 5.1): chỉ số lớn nhất có `charge >= tierMinCharge[i]`, cộng 1. */
export function essenceTier(charge: number, config: Pick<Config, 'essence'>): number {
  const mins = config.essence.tierMinCharge;
  let tier = 1;
  for (let i = 0; i < mins.length; i++) if (charge >= mins[i]!) tier = i + 1;
  return tier;
}

export function essenceRadius(charge: number, config: Pick<Config, 'essence'>): number {
  const radii = config.essence.radiusByTier;
  const tier = essenceTier(charge, config);
  return radii[Math.min(tier, radii.length) - 1] ?? 0;
}

/** Charge sau khi gộp (orbs.md 5.2). */
export function mergedCharge(charges: readonly number[], config: Pick<Config, 'essence'>): number {
  const total = charges.reduce((a, b) => a + b, 0);
  const bonus = Math.floor((total * config.essence.mergeBonusPct) / 100);
  return Math.min(config.essence.maxCharge, total + bonus);
}

/** Ngọc ấn nổ (orbs.md 5.3): sát thương ability trung tính, không crit, áp `res`. */
export function essenceDamage(
  abilityAttack: number,
  charge: number,
  targetRes: number,
  config: Pick<Config, 'combat' | 'essence'>,
): DamageSteps {
  const e1 = Math.floor((abilityAttack * config.essence.powerPct) / 100);
  const e2 = e1 * charge;
  const e3 = Math.floor((e2 * (100 + (charge - 1) * config.essence.superlinearPct)) / 100);
  const v = applyDefense(e3, targetRes, config);
  return { steps: [e1, e2, e3, v], value: v };
}
